// SuiteScanner design tokens — our design system (Deep Blue Royal #163ABA),
// authored in the server monorepo's style (barrel export, single quotes).
// The reference UI uses the bundled Metropolis family. Keep these names in
// tokens so every screen shares the same metrics and weight rendering.

const colors = {
    cream: '#F8FAFC',
    card: '#FFFFFF',
    ink: '#0F172A',
    inkSoft: '#475569',
    inkFaint: '#64748B',
    brand: '#163ABA',
    brandDeep: '#102C8C',
    brandBright: '#4D78FF',
    indigo: '#6366F1',
    skyTint: '#E8EDFF',
    pink: '#FF2D95',
    pinkTint: '#FFE6F0',
    green: '#0B6B3A',
    greenTint: '#A5F6C9',
    navy: '#062F43',
    indigoTint: '#ECEBFE',
    line: '#E2E8F0',
    lineSoft: '#EEF2F7',
    // Subtle blue hairline for the top-nav bottom border, which doubles as the
    // resting track for the navigation/API loading bar.
    brandLine: '#CBD8F5',
    white: '#FFFFFF'
} as const;

const radii = {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
    full: 9999
} as const;

const spacing = (n: number) => n * 8;

const maxContentWidth = 1360;

// Horizontal gutter every customer-facing page shell uses inside
// `maxContentWidth`. Named so screens stop re-deriving it as spacing(3).
const pageGutter = spacing(3);

const shadows = {
    sm: { boxShadow: '0px 1px 2px rgba(15, 23, 42, 0.05)' },
    card: { boxShadow: '0px 8px 24px rgba(15, 23, 42, 0.08)' },
    lift: { boxShadow: '0px 20px 40px rgba(15, 23, 42, 0.12)' }
} as const;

const fontFamily = {
    regular: 'Metropolis-Regular',
    medium: 'Metropolis-Medium',
    semibold: 'Metropolis-SemiBold',
    bold: 'Metropolis-Bold'
} as const;

const displayFontFamily = {
    regular: 'Georgia',
    italic: 'Georgia'
} as const;

// ── Typography ──────────────────────────────────────────────────────────────
// Semantic size/line-height pairs, and deliberately nothing else. These tokens
// carry no fontFamily, weight, colour, letterSpacing or alignment so that
// spreading one onto an existing Text normalises its metrics without
// disturbing the Metropolis weight it was already assigned or the Georgia
// italic accent inside a heading. Weight stays the component's decision.
const type = {
    /** Homepage hero headline. */
    display: { fontSize: 40, lineHeight: 48 },
    /** The single H1-equivalent title of a page (hotel name, Account, Help). */
    pageTitle: { fontSize: 28, lineHeight: 36 },
    /** Band/section headings inside a page. */
    sectionTitle: { fontSize: 22, lineHeight: 28 },
    /** Headings nested inside a section or card. */
    subsectionTitle: { fontSize: 18, lineHeight: 24 },
    /** Hotel/room/product name inside a card or row. Distinguished from
     *  bodyLarge by weight, not size — the same convention Booking.com uses. */
    cardTitle: { fontSize: 16, lineHeight: 22 },
    /** Lead paragraphs, input values, summary figures. */
    bodyLarge: { fontSize: 16, lineHeight: 24 },
    /** The workhorse. Addresses, policy lines, amenities, descriptions,
     *  supporting copy — anything the guest actually reads. */
    bodyRegular: { fontSize: 14, lineHeight: 20 },
    /** Fine print only: price suffixes, taxes notes, timestamps, badge text.
     *  12px is the floor — nothing in the customer UI renders smaller. */
    metadata: { fontSize: 12, lineHeight: 16 }
} as const;

// Narrow-viewport counterparts for the roles that must shrink. Roles absent
// here (bodyLarge and smaller) intentionally hold their desktop size at every
// width — 16/14/12 are already the legible floor and must not shrink further.
// Apply via the screen's existing `narrow`/`stacked`/`compact` flag; this does
// not introduce a breakpoint of its own.
const typeCompact = {
    display: { fontSize: 28, lineHeight: 34 },
    pageTitle: { fontSize: 24, lineHeight: 30 },
    sectionTitle: { fontSize: 20, lineHeight: 26 },
    subsectionTitle: { fontSize: 18, lineHeight: 24 },
    cardTitle: { fontSize: 16, lineHeight: 22 }
} as const;

// ── Price typography ────────────────────────────────────────────────────────
// Prices carry the purchase decision, so they get their own scale rather than
// borrowing heading sizes. Colour, weight, strikethrough and currency
// formatting stay with the component.
const price = {
    /** Homepage featured rail — the loudest price on the site. */
    featured: { fontSize: 30, lineHeight: 35 },
    /** Search result row listing price. */
    listing: { fontSize: 24, lineHeight: 29 },
    /** Carousel/collection cards, which sit in a denser rail. */
    compact: { fontSize: 22, lineHeight: 27 },
    /** Hotel detail hero price. */
    hero: { fontSize: 24, lineHeight: 29 },
    /** Per-rate price inside a room card. */
    roomRate: { fontSize: 18, lineHeight: 23 },
    /** The public/competitor price we compare against. */
    publicCompare: { fontSize: 16, lineHeight: 21 },
    /** Final amount payable at checkout and on the confirmation. */
    bookingTotal: { fontSize: 19, lineHeight: 25 },
    /** "Save $120" pills. */
    savingsPill: { fontSize: 12, lineHeight: 16 }
} as const;

// ── Control typography ──────────────────────────────────────────────────────
// Buttons, inputs, pills and badges are single-line text inside containers
// whose height is already set by padding or an explicit height. They get a
// size only — no lineHeight — so normalising the text cannot silently resize
// a control.
const control = {
    primaryCta: { fontSize: 16 },
    secondaryCta: { fontSize: 14 },
    cardCta: { fontSize: 14 },
    utilityCta: { fontSize: 12 },
    inputValue: { fontSize: 16 },
    inputLabel: { fontSize: 12 },
    inputHelper: { fontSize: 12 },
    inputError: { fontSize: 12 },
    badge: { fontSize: 12 },
    badgeCompact: { fontSize: 12 }
} as const;

// ── Spacing ─────────────────────────────────────────────────────────────────
// Named roles over the existing 8px `spacing()` function. The function stays
// the primitive; these say which multiple a given kind of component should
// reach for, which is the part that was previously left to each author.
const space = {
    /** Icon-to-label, badge internals. */
    micro: spacing(0.5), // 4
    /** Closely related elements; label-to-input. */
    related: spacing(1), // 8
    /** Compact text groups; image-to-title in a borderless card. */
    compact: spacing(1.5), // 12
    /** Standard internal gaps; distinct information groups in a card. */
    internal: spacing(2), // 16
    /** Standard component spacing; distinct field groups. */
    component: spacing(2.5), // 20
    /** Standard card padding; heading group to first content element. */
    card: spacing(3), // 24
    /** Large card padding; major form sections. */
    cardLarge: spacing(4), // 32
    /** Section separation. */
    section: spacing(5), // 40
    /** Major page-section separation; page bottom padding. */
    sectionMajor: spacing(6), // 48
    /** Large homepage band separation. */
    band: spacing(8) // 64
} as const;

const theme = {
    colors,
    radii,
    spacing,
    space,
    pageGutter,
    maxContentWidth,
    shadows,
    fontFamily,
    displayFontFamily,
    type,
    typeCompact,
    price,
    control
};

type Theme = typeof theme;

export {
    colors,
    control,
    displayFontFamily,
    fontFamily,
    maxContentWidth,
    pageGutter,
    price,
    radii,
    shadows,
    space,
    spacing,
    theme,
    type,
    typeCompact
};
export type { Theme };
